import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/locations", label: "Find a Location" },
    { href: "/memberships", label: "Memberships" },
    { href: "/about-us", label: "Why Us" },
    { href: "/contact-us", label: "Contact Us" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      {/*
      <div className="bg-red-700 text-white text-center px-4 py-3 text-sm md:text-base font-semibold">
        Woodbury Heights electric is currently out. Your membership will be honored at any Giant Fitness location.
      </div>
      */}
      <nav className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/images/GiantFitness-more-equipment-no-waiting-LOGO.png" 
              alt="Giant Fitness" 
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm uppercase tracking-wider font-medium transition-colors whitespace-nowrap ${
                  isActive(link.href) 
                    ? "text-brand-orange" 
                    : "text-gray-700 hover:text-brand-orange"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/locations">
              <Button 
                className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase tracking-wider rounded-lg shadow-soft hover:shadow-neon-orange transition-all duration-200 hover:-translate-y-0.5"
                data-testid="nav-join-now-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Join Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-3 text-gray-900"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 text-lg uppercase tracking-wider font-medium ${
                    isActive(link.href) 
                      ? "text-brand-orange" 
                      : "text-gray-600"
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/locations" onClick={() => setIsOpen(false)}>
                <Button 
                  className="w-full mt-4 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase tracking-wider"
                  data-testid="mobile-join-now-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Join Now
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
